import type {
  ContentLanguageRequest,
  DeleteWorksapceFeedItemResponse,
  GetWorkspaceFeedPermissionsResponse,
  NotificationFeedPreference,
  PublishTTSRequest,
  PublishTTSResponse,
  UpdateFeedPreferenceByFeedGroupResponse,
  UpdateFeedPreferenceResponse,
  WorkspaceFeedItemContentResponse,
  WorkspaceFeedItemLocationsResponse,
  WorkspaceFeedItemOldestEventsResponse,
} from "../web-client/api/data-contracts";
import * as operations from "../web-client/api/operations";
import type { Transport } from "../web-client/transport";

export function deleteWorkspaceFeedItem(
  transport: Transport,
  { workspaceId, feedId, itemId }: { workspaceId: string; feedId: string; itemId: string },
): Promise<DeleteWorksapceFeedItemResponse> {
  return operations.deleteWorkspacesItems(transport, { workspaceId, feedId, itemId });
}

export function publishTTS(
  transport: Transport,
  { workspaceId, feedId, ...body }: { workspaceId: string; feedId: string } & PublishTTSRequest,
): Promise<PublishTTSResponse> {
  return operations.postWorkspacesTts(transport, { workspaceId, feedId, body });
}

export function getFeedItemContent(
  transport: Transport,
  {
    workspaceId,
    itemIds,
    preferredLanguage,
    timestamp,
  }: { workspaceId: string; itemIds: string[]; preferredLanguage?: string; timestamp?: string },
): Promise<WorkspaceFeedItemContentResponse> {
  const language: ContentLanguageRequest =
    preferredLanguage === "none" ? "original" : (preferredLanguage as ContentLanguageRequest);
  return operations.postWorkspacesFeedItemContent(transport, { workspaceId, body: { itemIds, language, timestamp } });
}

export function getFeedItemOldestEvents(
  transport: Transport,
  {
    workspaceId,
    itemIds,
    page,
    pageSize,
    timestamp,
  }: { workspaceId: string; itemIds: string[]; page?: number; pageSize?: number; timestamp?: string },
): Promise<WorkspaceFeedItemOldestEventsResponse> {
  return operations.postWorkspacesFeedItemOldestEvents(transport, {
    workspaceId,
    page,
    pageSize,
    timestamp,
    body: { itemIds },
  });
}

export function getLocationsFromItemIds(
  transport: Transport,
  { workspaceId, itemIds }: { workspaceId: string; itemIds: string[] },
): Promise<WorkspaceFeedItemLocationsResponse> {
  return operations.postWorkspacesFeedItemsLocations(transport, { workspaceId, body: { itemIds } });
}

export function getWorkspaceFeedPermissions(
  transport: Transport,
  { workspaceId, feedId, page, pageSize }: { workspaceId: string; feedId: string; page?: number; pageSize?: number },
): Promise<GetWorkspaceFeedPermissionsResponse> {
  return operations.getWorkspacesFeedsPermissions(transport, { workspaceId, feedId, page, pageSize });
}

export function subscribeToFeed(
  transport: Transport,
  {
    workspaceId,
    feedId,
    workspaceMembershipId,
    preference,
  }: { workspaceId: string; feedId: string; workspaceMembershipId: string; preference: NotificationFeedPreference },
): Promise<UpdateFeedPreferenceResponse> {
  return updateFeedPreferences(transport, workspaceId, feedId, workspaceMembershipId, preference);
}

export function unsubscribeFromFeed(
  transport: Transport,
  {
    workspaceId,
    feedId,
    workspaceMembershipId,
  }: { workspaceId: string; feedId: string; workspaceMembershipId: string },
): Promise<UpdateFeedPreferenceResponse> {
  return updateFeedPreferences(transport, workspaceId, feedId, workspaceMembershipId, "none");
}

function updateFeedPreferences(
  transport: Transport,
  workspaceId: string,
  feedId: string,
  membershipId: string,
  preference: NotificationFeedPreference,
): Promise<UpdateFeedPreferenceResponse> {
  return operations.patchWorkspacesFeedsMembershipPreferences(transport, {
    workspaceId,
    feedId,
    membershipId,
    body: { pushNotificationPreference: preference, handsFreeNotificationPreference: preference },
  });
}

export function subscribeToFeedGroup(
  transport: Transport,
  {
    workspaceId,
    feedGroupId,
    workspaceMembershipId,
    preference,
  }: {
    workspaceId: string;
    feedGroupId: string;
    workspaceMembershipId: string;
    preference: NotificationFeedPreference;
  },
): Promise<UpdateFeedPreferenceByFeedGroupResponse> {
  return updateFeedGroupPreferences(transport, workspaceId, feedGroupId, workspaceMembershipId, preference);
}

export function unsubscribeFromFeedGroup(
  transport: Transport,
  {
    workspaceId,
    feedGroupId,
    workspaceMembershipId,
  }: { workspaceId: string; feedGroupId: string; workspaceMembershipId: string },
): Promise<UpdateFeedPreferenceByFeedGroupResponse> {
  return updateFeedGroupPreferences(transport, workspaceId, feedGroupId, workspaceMembershipId, "none");
}

function updateFeedGroupPreferences(
  transport: Transport,
  workspaceId: string,
  feedGroupId: string,
  membershipId: string,
  preference: NotificationFeedPreference,
): Promise<UpdateFeedPreferenceByFeedGroupResponse> {
  return operations.patchWorkspacesFeedGroupsMembershipPreferences(transport, {
    workspaceId,
    feedGroupId,
    membershipId,
    body: { pushNotificationPreference: preference, handsFreeNotificationPreference: preference },
  });
}

export function subscribeToDriverFeeds(
  transport: Transport,
  {
    workspaceId,
    workspaceMembershipId,
    driverWorkspaceMembershipIds,
  }: { workspaceId: string; workspaceMembershipId: string; driverWorkspaceMembershipIds: string[] },
): Promise<UpdateFeedPreferenceByFeedGroupResponse> {
  return operations.postWorkspacesMembershipDriverFeedPreferences(transport, {
    workspaceId,
    membershipId: workspaceMembershipId,
    body: { workspaceMembershipIds: driverWorkspaceMembershipIds },
  });
}

export function unsubscribeFromDriverFeeds(
  transport: Transport,
  {
    workspaceId,
    workspaceMembershipId,
    driverWorkspaceMembershipIds,
  }: { workspaceId: string; workspaceMembershipId: string; driverWorkspaceMembershipIds: string[] },
): Promise<UpdateFeedPreferenceByFeedGroupResponse> {
  return operations.deleteWorkspacesMembershipDriverFeedPreferences(transport, {
    workspaceId,
    membershipId: workspaceMembershipId,
    body: { workspaceMembershipIds: driverWorkspaceMembershipIds },
  });
}
